import { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { numberWithCommas } from "../../utils/functions";
import { BsInfoCircleFill } from "react-icons/bs";
import useModalStore from '../../stores/ModalStore';
import FeeBreakdownModal from '../../modals/FeeBreakdownModal/FeeBreakdownModal';
import Spinner from '../Spinner/Spinner';

import "./FeeLimits.css";

interface FeeLimitsProps {
    ticker: string;
    amount?: number;
    minLimit: number;
    maxLimit: number;
    fee?: number;
    minFee?: number;
    isPending?: boolean;
    flat?: boolean;
    onLimitCheck: (isValid: boolean, minLimitWithFee?: number) => void;
    buttonAction?: () => void;
    decimals?: number;
}

const FeeLimits = ({
    ticker,
    isPending = false,
    amount = 0,
    minLimit,
    maxLimit,
    fee = 0,
    minFee = 0,
    onLimitCheck,
    buttonAction,
    decimals = 8,
}: FeeLimitsProps) => {
    const { t } = useTranslation('common');
    const [onLimit, setOnLimit] = useState(false);
    const [isCalculating, setIsCalculating] = useState(false);
    const [displayAmount, setDisplayAmount] = useState(0);
    const [pendingAmount, setPendingAmount] = useState<number | null>(null); // Track typing amount

    const { setModalContent } = useModalStore();

    const showFeeBreakdown = () => {
        setModalContent(
            <FeeBreakdownModal
                amount={amount}
                fee={fee}
                minFee={minFee}
                ticker={ticker}
                decimals={decimals}
            />
        );
    };

    useEffect(() => {
        if (amount > 0) {
            setPendingAmount(amount); // Update pending amount while typing
            setIsCalculating(true); // Show spinner immediately
        } else {
            setPendingAmount(null);
            setDisplayAmount(0);
            setIsCalculating(false);
            setOnLimit(false);
            onLimitCheck(false, getLimitWithFee(minLimit));
        }
    }, [amount, minLimit, minFee, fee, decimals, onLimitCheck]);

    useEffect(() => {
        if (pendingAmount === null) return;

        const timer = setTimeout(() => {
            const calculatedFee = Math.max(minFee, pendingAmount * fee);
            const amountToReceive = parseFloat((pendingAmount - calculatedFee).toFixed(decimals));
            const isValid = amountToReceive >= minLimit && amountToReceive <= maxLimit && amountToReceive > 0;
            const minLimitWithFee = getLimitWithFee(minLimit);

            setDisplayAmount(amountToReceive);
            setIsCalculating(false); // Hide spinner after 1 second
            setOnLimit(isValid);
            onLimitCheck(isValid, minLimitWithFee);
        }, 500); 

        return () => clearTimeout(timer);
    }, [pendingAmount, fee, minLimit, maxLimit, minFee, decimals, onLimitCheck]);

    const amountClass = pendingAmount !== null || amount === 0
        ? 'amount-within-limits' // Neutral while typing or no amount
        : (onLimit ? 'amount-within-limits' : 'amount-outside-limits');

    const getLimitWithFee = (limit: number) => {
        const calculatedFee = Math.max(minFee, limit * fee);
        return limit + calculatedFee;
    };

    const minLimitWithFee = getLimitWithFee(minLimit);
    const maxLimitWithFee = getLimitWithFee(maxLimit);
    const appliedFee = Math.max(minFee, amount * fee);

    return (
        <div className="fee-main-cont">
            <div className="fee-limits-cont">
                {minLimit !== 0 && maxLimit !== 0 && amount > 0 && (
                    <div className="limits-section">
                        <label className="limit-label">{t("limits")}</label>
                        <div className="limits-with-fee">
                            <div className="limit-item">
                                <span className="limit-type">{t("min_withdrawal")}:</span>
                                <span className={`limit-value ${amountClass}`}>
                                    {numberWithCommas(minLimitWithFee, decimals)} {ticker}
                                </span>
                            </div>
                            <div className="limit-item">
                                <span className="limit-type">{t("max_withdrawal")}:</span>
                                <span className={`limit-value ${amountClass}`}>
                                    {numberWithCommas(maxLimitWithFee, decimals)} {ticker}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
                {amount > 0 && amount >= minLimitWithFee && (
                    <div className="receive-amount-section" onClick={showFeeBreakdown}>
                        <span className="receive-amount-label">
                            {t("recieve_amount")}:
                        </span>
                        <div className="receive-amount-wrapper">
                            {isCalculating ? (
                                <div className="amount-loading">
                                    <Spinner />
                                </div>
                            ) : (
                                <span className="receive-amount-value">
                                    {numberWithCommas(displayAmount, decimals)} {ticker}
                                </span>
                            )}
                            {appliedFee > 0 && !isCalculating && (
                                <div className="fee-breakdown-container">
                                    <button
                                        className="fee-breakdown-button"
                                        onClick={showFeeBreakdown}
                                        aria-label="Show fee breakdown"
                                    >
                                        <BsInfoCircleFill className="info-icon" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
            {buttonAction && (
                <div className="fees-limits-button">
                    <button
                        disabled={isPending || !onLimit}
                        className="fee-action-button"
                        onClick={buttonAction}
                    >
                        {t("continue")}
                    </button>
                </div>
            )}
        </div>
    );
};

export default FeeLimits;